/**
 * Animations helper for vanilla JS (jQuery replacements)
 * This module provides slideUp, slideDown, slideToggle, fadeIn, fadeOut and fadeToggle
 * to replace jQuery animations.
 * Any custom animation can be added here.
 */

const Animations = {
  /**
   * Slide up animation (equivalent to jQuery's slideUp)
   * @param {HTMLElement} element - The element to animate
   * @param {number} duration - Animation duration in milliseconds
   * @param {Function} callback - Optional callback after animation
   */
  slideUp: (element, duration = 300, callback = null) => {
    if (!element) return;

    element.style.height = `${element.offsetHeight}px`;
    element.style.transitionProperty = 'height, margin, padding';
    element.style.transitionDuration = `${duration}ms`;
    element.style.overflow = 'hidden';

    // Force repaint
    element.offsetHeight;

    element.style.height = '0';
    element.style.paddingTop = '0';
    element.style.paddingBottom = '0';
    element.style.marginTop = '0';
    element.style.marginBottom = '0';

    setTimeout(() => {
      element.style.display = 'none';
      element.style.removeProperty('height');
      element.style.removeProperty('padding-top');
      element.style.removeProperty('padding-bottom');
      element.style.removeProperty('margin-top');
      element.style.removeProperty('margin-bottom');
      element.style.removeProperty('overflow');
      element.style.removeProperty('transition-duration');
      element.style.removeProperty('transition-property');
      if (callback) callback();
    }, duration);
  },

  /**
   * Slide down animation (equivalent to jQuery's slideDown)
   * @param {HTMLElement} element - The element to animate
   * @param {number} duration - Animation duration in milliseconds
   * @param {Function} callback - Optional callback after animation
   */
  slideDown: (element, duration = 300, callback = null) => {
    if (!element) return;

    element.style.removeProperty('display');
    let display = window.getComputedStyle(element).display;
    if (display === 'none') display = 'block';
    element.style.display = display;

    const height = element.offsetHeight;
    element.style.overflow = 'hidden';
    element.style.height = '0';
    element.style.paddingTop = '0';
    element.style.paddingBottom = '0';
    element.style.marginTop = '0';
    element.style.marginBottom = '0';

    // Force repaint
    element.offsetHeight;

    element.style.transitionProperty = 'height, margin, padding';
    element.style.transitionDuration = `${duration}ms`;
    element.style.height = `${height}px`;
    element.style.removeProperty('padding-top');
    element.style.removeProperty('padding-bottom');
    element.style.removeProperty('margin-top');
    element.style.removeProperty('margin-bottom');

    setTimeout(() => {
      element.style.removeProperty('height');
      element.style.removeProperty('overflow');
      element.style.removeProperty('transition-duration');
      element.style.removeProperty('transition-property');
      if (callback) callback();
    }, duration);
  },

  /**
   * Slide toggle animation (equivalent to jQuery's slideToggle)
   * @param {HTMLElement} element - The element to animate
   * @param {number} duration - Animation duration in milliseconds
   * @param {Function} callback - Optional callback after animation
   */
  slideToggle: (element, duration = 300, callback = null) => {
    if (!element) return;

    if (window.getComputedStyle(element).display === 'none') {
      return Animations.slideDown(element, duration, callback);
    }
    return Animations.slideUp(element, duration, callback);
  },

  /**
   * Fade in animation (equivalent to jQuery's fadeIn)
   * @param {HTMLElement} element - The element to animate
   * @param {number} duration - Animation duration in milliseconds
   * @param {string} display - Display style after animation
   * @param {Function} callback - Optional callback after animation
   */
  fadeIn: (element, duration = 300, display = 'block', callback = null) => {
    if (!element) return;

    element.style.opacity = 0;
    element.style.display = display;

    // Force repaint
    element.offsetHeight;

    element.style.transition = `opacity ${duration}ms`;
    element.style.opacity = 1;

    setTimeout(() => {
      element.style.removeProperty('transition');
      if (callback) callback();
    }, duration);
  },

  /**
   * Fade out animation (equivalent to jQuery's fadeOut)
   * @param {HTMLElement} element - The element to animate
   * @param {number} duration - Animation duration in milliseconds
   * @param {Function} callback - Optional callback after animation
   */
  fadeOut: (element, duration = 300, callback = null) => {
    if (!element) return;

    element.style.opacity = 1;

    // Force repaint
    element.offsetHeight;

    element.style.transition = `opacity ${duration}ms`;
    element.style.opacity = 0;

    setTimeout(() => {
      element.style.display = 'none';
      element.style.removeProperty('transition');
      element.style.removeProperty('opacity');
      if (callback) callback();
    }, duration);
  },

  /**
   * Fade toggle animation (equivalent to jQuery's fadeToggle)
   * @param {HTMLElement} element - The element to animate
   * @param {number} duration - Animation duration in milliseconds
   * @param {string} display - Display style after animation
   * @param {Function} callback - Optional callback after animation
   */
  fadeToggle: (element, duration = 300, display = 'block', callback = null) => {
    if (!element) return;

    if (window.getComputedStyle(element).display === 'none') {
      return Animations.fadeIn(element, duration, display, callback);
    }
    return Animations.fadeOut(element, duration, callback);
  },
};

export default Animations;
